import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';

export interface CostCenterLookup {
  id: string;
  code: string;
  name: string;
  corporation_id?: string;
  cost_center_type?: string;
}

interface ListResponse {
  success: boolean;
  data: CostCenterLookup[];
}

export interface UseCostCentersParams {
  corporation_id?: string;
  search?: string;
  limit?: number;
}

/**
 * Lookup-only Hook gegen pm-api `/api/v1/lookups/cost-centers`.
 * Bei gesetztem `corporation_id` schaltet pm-api intern auf
 * `/corporations/:id/cost-centers` um (sonst flacher Endpoint).
 */
export function useCostCenters(params?: UseCostCentersParams) {
  return useQuery<ListResponse>({
    queryKey: ['lookups', 'cost-centers', params],
    queryFn: () => {
      const query: Record<string, unknown> = {};
      if (params?.corporation_id) query.corporation_id = params.corporation_id;
      if (params?.search) query.search = params.search;
      if (params?.limit !== undefined) query.limit = params.limit;
      return apiClient.get<CostCenterLookup[]>('/api/v1/lookups/cost-centers', query);
    },
    staleTime: 30_000,
  });
}

/**
 * Convenience: alle Cost-Center einer Corporation.
 * Disabled wenn keine Corporation-ID gesetzt ist — verhindert sinnlose
 * Full-Scan-Calls beim initialen Render von Form-Dialogen.
 */
export function useCorporationCostCenters(corporationId: string | undefined) {
  return useQuery<ListResponse>({
    queryKey: ['lookups', 'cost-centers', 'by-corporation', corporationId],
    queryFn: () => apiClient.get<CostCenterLookup[]>('/api/v1/lookups/cost-centers', {
      corporation_id: corporationId,
    }),
    enabled: !!corporationId,
    staleTime: 30_000,
  });
}
